import type { JSX } from 'react';

import { Chip } from '../ds';
import { CardTaskRow } from '../opening-tasks/CardTaskRow';
import { fmtWindow, type PlanMode, type SideFilter, type TaskGroup } from './plan-model';

/**
 * Подписи стороны: «Мои» — партнёр и «обе стороны», «УК» — только управляющая компания. Менеджеру «Мои» не подходит
 * (это не его задачи) — там «Партнёр».
 */
const SIDE_LABEL: Record<PlanMode, Record<SideFilter, string>> = {
  partner: { mine: 'Мои', uk: 'УК', all: 'Все' },
  manager: { mine: 'Партнёр', uk: 'УК', all: 'Все' },
};
const SIDE_ORDER: SideFilter[] = ['mine', 'uk', 'all'];

/** Ряд чипов «Сторона: Мои | УК | Все»; `counts` — сколько задач даст каждый вариант. */
export function SideChips({
  value,
  onChange,
  counts,
  mode = 'partner',
}: {
  value: SideFilter;
  onChange: (next: SideFilter) => void;
  counts: Record<SideFilter, number>;
  mode?: PlanMode;
}): JSX.Element {
  return (
    <div className="fk-op-frow" role="group" aria-label="Чьи задачи">
      <span className="fk-op-flabel">Чьи</span>
      {SIDE_ORDER.map((k) => (
        <Chip key={k} active={value === k} count={counts[k]} onClick={() => onChange(k)}>
          {SIDE_LABEL[mode][k]}
        </Chip>
      ))}
    </div>
  );
}

/**
 * Задачи по этапам: у каждой группы — заголовок (этап, окно дат, «x/y») и строки `CardTaskRow` (те же, что в
 * «Моих задачах»: отметка «Сделано», чек-лист, срок, вехи, хвосты, статья базы знаний, ссылки). `defaultOpen` —
 * строки раскрыты сразу (вкладка «Чеклисты»). `readOnly` — режим «смотреть от лица».
 */
export function PlanTaskGroups({
  groups,
  readOnly,
  defaultOpen = false,
  doableIds,
}: {
  groups: TaskGroup[];
  readOnly: boolean;
  defaultOpen?: boolean;
  /** id задач, которые «можно делать на этой неделе» (FB-32); не передан — ярлыков нет. */
  doableIds?: ReadonlySet<string>;
}): JSX.Element {
  return (
    <div className="fk-op-groups">
      {groups.map((g) => {
        const win = fmtWindow(g.start, g.end, g.estimated && g.state !== 'done');
        return (
          <section key={g.code} className="fk-op-card" aria-label={g.label}>
            <div className="fk-op-ghead">
              <span className="fk-op-gname">
                {g.label}
                {g.state === 'current' ? <em>сейчас</em> : null}
              </span>
              {win ? <span className="fk-op-gsub">{win}</span> : null}
              <span className="fk-op-gcount" title="Выполнено / всего задач этапа">
                {g.done}/{g.total}
              </span>
            </div>
            <div>
              {g.tasks.map((t, i) => (
                <CardTaskRow key={t.id} task={t} index={i} readOnly={readOnly} defaultOpen={defaultOpen} doableThisWeek={doableIds?.has(t.id) ?? false} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
